// apps/web/src/pages/ThreatActorsPage.tsx
//
// KAN-221 — Threat Actors as a CISO-grade intelligence desk (not a uniform card
// grid). Composition mirrors the IntelHub Dashboard pattern:
//   header  — title, Actor Registry pill, purpose subtitle, freshness, ⌘K
//   pulse   — supported corpus-wide attribution KPIs from /kpis
//   watch   — evidence-led editorial highlights (corpus + loaded-view)
//   feed    — server-backed filter workbench + ranked dossier list
//   rail    — loaded-view source coverage
//
// Data wiring is unchanged: useThreatActors (list, capped 50) + useThreatActorKpis
// (corpus aggregate). This page stays orchestration; logic lives in feature-local
// components and pure helpers under components/threat-actors/.

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { ThreatActorPulseStrip } from '@/components/threat-actors/ThreatActorPulseStrip'
import { ThreatActorHighlightsBand } from '@/components/threat-actors/ThreatActorHighlightsBand'
import { ThreatActorFilterWorkbench } from '@/components/threat-actors/ThreatActorFilterWorkbench'
import { ThreatActorDossierList } from '@/components/threat-actors/ThreatActorDossierList'
import { ThreatActorContextRail } from '@/components/threat-actors/ThreatActorContextRail'
import { kpiTotal } from '@/components/threat-actors/threat-actor-utils'
import { useThreatActors, useThreatActorKpis } from '@/hooks/queries'
import { useFilterDefaultsFromPrefs } from '@/hooks/useFilterDefaultsFromPrefs'
import { formatDate } from '@/lib/utils'
import {
  DEFAULT_THREAT_ACTOR_SORT,
  THREAT_ACTOR_SORT_OPTIONS,
  type ThreatActorFilters,
  type ThreatActorSort,
} from '@/lib/types'

function parseSort(raw: string | null): ThreatActorSort {
  if (!raw) return DEFAULT_THREAT_ACTOR_SORT
  return (THREAT_ACTOR_SORT_OPTIONS as readonly string[]).includes(raw)
    ? (raw as ThreatActorSort)
    : DEFAULT_THREAT_ACTOR_SORT
}

function openCommandPalette() {
  document.dispatchEvent(new CustomEvent('intelhub:open-command-palette'))
}

export function ThreatActorsPage() {
  // P5 adoption — seed filter state from the user's saved 'threat-actors'
  // search; fall back to the global view. Enforced by the
  // `filter-rail-must-adopt-prefs` ESLint rule.
  const { defaults: prefDefaults } =
    useFilterDefaultsFromPrefs<ThreatActorFilters>('threat-actors')

  const [attribution, setAttribution] = useState<string>(
    prefDefaults?.attribution ?? 'all',
  )

  const [searchParams, setSearchParams] = useSearchParams()
  const sort = parseSort(searchParams.get('sort'))

  const updateSort = (next: ThreatActorSort) => {
    const params = new URLSearchParams(searchParams)
    if (next === DEFAULT_THREAT_ACTOR_SORT) {
      params.delete('sort')
    } else {
      params.set('sort', next)
    }
    setSearchParams(params, { replace: false })
  }

  const filterArgs = {
    attribution: attribution !== 'all' ? attribution : undefined,
  }

  const {
    data: actors,
    isLoading,
    isError,
    refetch,
  } = useThreatActors({ ...filterArgs, sort })

  // Corpus-wide aggregate (not saturated at the list endpoint's DEFAULT_LIMIT).
  const { data: kpis } = useThreatActorKpis(filterArgs)

  const hasActiveFilters =
    attribution !== 'all' ||
    sort !== DEFAULT_THREAT_ACTOR_SORT

  const handleReset = () => {
    setAttribution('all')
    updateSort(DEFAULT_THREAT_ACTOR_SORT)
  }

  // Result label — honest about the 50-row list cap vs the corpus total.
  const loadedCount = actors?.length ?? 0
  const registryTotal = kpis ? kpiTotal(kpis) : null
  const resultLabel = isLoading
    ? 'Loading…'
    : registryTotal != null && loadedCount < registryTotal
      ? `${loadedCount} of ${registryTotal} loaded`
      : `${loadedCount} loaded`

  // Source freshness — only show a timestamp we can truthfully derive.
  const latestSync = (actors ?? [])
    .map((a) => a.sourcesLastSyncedAt)
    .filter((v): v is string => Boolean(v))
    .sort()
    .pop()
  const syncedLabel = latestSync ? `Sources synced ${formatDate(latestSync)}` : null

  return (
    <main className="mx-auto flex max-w-[1440px] flex-col gap-7 p-4 md:p-6">
      <header className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-2xl font-light tracking-tight">
              Threat Actors
            </h1>
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
              Actor Registry
            </span>
          </div>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Adversary profiles with published attribution and source identifiers —
            evidence first, unsupported interpretation omitted.
          </p>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-3">
          {syncedLabel ? (
            <span
              className="inline-flex items-center gap-2 whitespace-nowrap font-mono text-[11px] text-muted-foreground"
              title={latestSync ?? undefined}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden="true" />
              {syncedLabel}
            </span>
          ) : null}
          <button
            type="button"
            onClick={openCommandPalette}
            className="inline-flex min-w-[210px] items-center gap-2 rounded-md border border-border bg-secondary px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Search className="h-3.5 w-3.5" aria-hidden="true" />
            Search actors, aliases, IDs…
            <kbd className="ml-auto rounded-sm border border-border px-1.5 py-0.5 font-mono text-[10px]">
              ⌘K
            </kbd>
          </button>
        </div>
      </header>

      <section aria-labelledby="ta-pulse" className="flex flex-col gap-3">
        <DashboardSectionHeader
          headingId="ta-pulse"
          label="Actor pulse"
          aux="Registry KPIs · corpus-wide"
        />
        <ThreatActorPulseStrip kpis={kpis} />
      </section>

      <section aria-labelledby="ta-watch" className="flex flex-col gap-3">
        <DashboardSectionHeader
          headingId="ta-watch"
          label="What to watch"
          aux="Analyst read of the current registry"
        />
        <ThreatActorHighlightsBand kpis={kpis} actors={actors} />
      </section>

      <div className="grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section aria-labelledby="ta-feed" className="flex flex-col gap-3">
          <DashboardSectionHeader
            headingId="ta-feed"
            label="Actor feed"
            aux={resultLabel}
          />
          <div>
            <ThreatActorFilterWorkbench
              attribution={attribution}
              sort={sort}
              hasActiveFilters={hasActiveFilters}
              onAttribution={setAttribution}
              onSort={updateSort}
              onReset={handleReset}
            />
            <ThreatActorDossierList
              actors={actors}
              isLoading={isLoading}
              isError={isError}
              hasActiveFilters={hasActiveFilters}
              onRetry={() => refetch()}
              onReset={handleReset}
            />
          </div>
        </section>

        <ThreatActorContextRail actors={actors} />
      </div>
    </main>
  )
}
